/**
 * NotificationSettings — page utilisateur des préférences de notifications.
 * Affiche uniquement les types de notif visibles pour le rôle de l'user connecté,
 * regroupés par catégorie, avec 3 toggles par type : cloche / email / digest.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Bell, Mail, Clock, Info, VolumeX, RotateCcw, Loader2, AlertTriangle, Send,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_COLORS = {
  info: "bg-blue-100 text-blue-700 border-blue-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};

export default function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState([]);
  const [role, setRole] = useState("");
  const [roleLabel, setRoleLabel] = useState("");
  const [saving, setSaving] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get("/notifications/types"),
        api.get("/notifications/preferences"),
      ]);
      setRole(t.data.role);
      setRoleLabel(t.data.role_label);
      setPrefs(p.data.preferences || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updatePref = async (type_code, patch) => {
    setSaving(type_code);
    setPrefs((old) => old.map((p) => p.type_code === type_code ? { ...p, ...patch } : p));
    try {
      await api.patch("/notifications/preferences", { type_code, ...patch });
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally { setSaving(null); }
  };

  const muteAll = async () => {
    if (!window.confirm("Couper tous les emails ? (Les notifications cloche restent actives.)")) return;
    setBulkAction("mute");
    try {
      const r = await api.post("/notifications/preferences/mute-all");
      toast.success(`${r.data.muted} type(s) coupé(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkAction(null); }
  };

  const resetAll = async () => {
    if (!window.confirm("Réinitialiser toutes les préférences aux valeurs par défaut ?")) return;
    setBulkAction("reset");
    try {
      const r = await api.post("/notifications/preferences/reset");
      toast.success(`${r.data.deleted} préférence(s) supprimée(s) — retour aux défauts`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkAction(null); }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/notifications/test-send");
      setTestResult({ ok: true, ...r.data });
      toast.success(`Email de test envoyé à ${r.data.recipient}`);
    } catch (e) {
      setTestResult({ ok: false, error: apiError(e) });
      toast.error(apiError(e));
    } finally { setTesting(false); }
  };

  // Groupé par category
  const byCategory = prefs.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Paramètres</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Notifications
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Vous voyez uniquement les types adressés à votre rôle : <b>{roleLabel}</b>.
          Chaque type peut être reçu dans la cloche (temps réel), par email (temps réel)
          ou groupé en digest quotidien.
        </p>
      </div>

      {/* Actions bulk */}
      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" onClick={sendTest} disabled={testing}
                data-testid="send-test-email-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
          {testing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Send className="h-3.5 w-3.5 mr-1.5" />}
          Envoyer un email de test
        </Button>
        <Button variant="outline" size="sm" onClick={muteAll} disabled={bulkAction === "mute"}
                data-testid="mute-all-btn">
          {bulkAction === "mute" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                  : <VolumeX className="h-3.5 w-3.5 mr-1.5" />}
          Couper tous les emails
        </Button>
        <Button variant="outline" size="sm" onClick={resetAll} disabled={bulkAction === "reset"}
                data-testid="reset-all-btn">
          {bulkAction === "reset" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
          Réinitialiser aux défauts
        </Button>
      </div>

      {/* Résultat test email */}
      {testResult && (
        <div className={`rounded-md border p-3 text-xs flex items-start gap-2 ${
          testResult.ok
            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : "border-red-200 bg-red-50 text-red-900"
        }`} data-testid="test-email-result">
          {testResult.ok ? (
            <>
              <Send className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                <b>Email envoyé ({testResult.provider})</b> — {testResult.message}
                {testResult.provider_message_id && (
                  <div className="text-[10px] mt-1 opacity-75 font-mono">
                    ID: {testResult.provider_message_id}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                <b>Envoi échoué</b> — {testResult.error}
              </div>
            </>
          )}
        </div>
      )}

      {/* Aide */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div>
          <b>Digest quotidien</b> : au lieu de recevoir un email à chaque événement,
          vous recevez un récap unique le lendemain matin. Utile pour les types
          fréquents (import SDD, facture à valider).
        </div>
      </div>

      {/* Groupes */}
      {Object.entries(byCategory).map(([category, types]) => (
        <Card key={category} data-testid={`notif-category-${category}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{category}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                <div className="col-span-6">Type de notification</div>
                <div className="col-span-2 text-center">Cloche</div>
                <div className="col-span-2 text-center">Email</div>
                <div className="col-span-2 text-center">Digest</div>
              </div>
              {types.map((p) => (
                <div key={p.type_code} className="grid grid-cols-12 gap-3 px-4 py-3 items-center"
                     data-testid={`notif-row-${p.type_code}`}>
                  <div className="col-span-6">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{p.label}</span>
                      {p.severity !== "info" && (
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SEVERITY_COLORS[p.severity]}`}>
                          {p.severity === "warning" ? "Vigilance" : "Critique"}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{p.type_code}</div>
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <Switch checked={p.in_app}
                            onCheckedChange={(v) => updatePref(p.type_code, { in_app: v })}
                            disabled={saving === p.type_code}
                            data-testid={`notif-toggle-inapp-${p.type_code}`} />
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <Switch checked={p.email}
                            onCheckedChange={(v) => updatePref(p.type_code, { email: v })}
                            disabled={saving === p.type_code}
                            data-testid={`notif-toggle-email-${p.type_code}`} />
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <Switch checked={p.digest}
                            onCheckedChange={(v) => updatePref(p.type_code, { digest: v })}
                            disabled={saving === p.type_code || !p.email}
                            data-testid={`notif-toggle-digest-${p.type_code}`} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {prefs.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-slate-500">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            Aucun type de notification adressé à votre rôle.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
